import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, CircularProgress, Alert, IconButton, Box, Typography, Rating
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { submitFeedback } from '../api/feedback';

const customIcons = {
  1: { icon: <span style={{ fontSize: '2rem' }}>🙂</span> },
  2: { icon: <span style={{ fontSize: '2rem' }}>😊</span> },
  3: { icon: <span style={{ fontSize: '2rem' }}>😃</span> },
  4: { icon: <span style={{ fontSize: '2rem' }}>😄</span> },
  5: { icon: <span style={{ fontSize: '2rem' }}>🤩</span> },
};

function IconContainer(props) {
  const { value, ...other } = props;
  return <span {...other}>{customIcons[value].icon}</span>;
}

const FeedbackDialog = ({ open, onClose }) => {
    const [feedbackText, setFeedbackText] = useState('');
    const [rating, setRating] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async () => {
        if (rating === 0) {
            setError('Please select a rating before submitting.');
            return;
        }
        setLoading(true);
        setError(null);
        setSuccess(false);
        try {
            const token = localStorage.getItem('jwtToken');
            await submitFeedback({ feedback_text: feedbackText, rating }, token);
            setSuccess(true);
            setFeedbackText('');
            setRating(0);
            setTimeout(() => {
                onClose();
                setTimeout(() => {
                    setSuccess(false);
                }, 300);
            }, 2000);
        } catch (err) {
            setError(err.message || 'Failed to submit feedback.');
        } finally {
            setLoading(false);
        }
    };

    const handleDialogClose = () => {
        if (loading) return;
        onClose();
        setError(null);
        setSuccess(false);
        setFeedbackText('');
        setRating(0);
    };

    return (
       <Dialog 
          open={open} 
          onClose={handleDialogClose} 
          fullWidth 
          maxWidth="sm" 
          PaperProps={{ sx: { borderRadius: 3, boxShadow: 6 } }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#11224d',
              color: 'white',
              py: 2,
              px: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              We Appreciate Your Feedback
            </Typography>
            <IconButton onClick={handleDialogClose} disabled={loading}>
              <CloseIcon sx={{ color: 'white' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 3, py: 2 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ my: 2, textAlign: 'center', color: 'text.secondary' }}
            >
              We are always looking for ways to improve your experience. 
              Please take a moment to evaluate and tell us what you think.
            </Typography>

            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mb: 3, 
                p: 2, 
                borderRadius: 2, 
                bgcolor: '#f9f9f9' 
              }}
            >
              <Rating
                name="feedback-rating"
                value={rating}
                onChange={(event, newValue) => setRating(newValue)}
                IconContainerComponent={IconContainer}
                size="large"
              />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>Thank you! Your feedback has been sent.</Alert>}

            <TextField
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              placeholder="What can we do to improve your experience?"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              disabled={loading || success}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'flex-end', p: 3 }}>
            <Button 
              onClick={handleDialogClose} 
              disabled={loading} 
              variant="outlined"
              sx={{ borderColor: '#11224d', color: '#11224d' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || success}
              sx={{
                backgroundColor: '#ffb300',
                color: '#11224d',
                fontWeight: 600,
                px: 3,
                '&:hover': { backgroundColor: '#ffa000' }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Submit My Feedback'}
            </Button>
          </DialogActions>
        </Dialog>
    );
};

export default FeedbackDialog;
